import { useSEO } from '@/hooks/useSEO';
import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import StoreHero from "./components/StoreHero";
import ProductGrid from "./components/ProductGrid";
import CartDrawer from "./components/CartDrawer";
import CheckoutModal from "./components/CheckoutModal";
import OrderConfirmation from "./components/OrderConfirmation";
import Footer from "@/pages/home/components/Footer";
import AnnouncementBanners from "@/components/feature/AnnouncementBanners";
import { useCart } from "./hooks/useCart";
import type { StoreProduct } from "@/mocks/storeProducts";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

const STORE_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Store",
    "name": "ISMAN Engineering Parts & Equipment Store",
    "url": SITE_URL + "/store",
    "description": "Shop engineering spare parts, consumables and equipment from ISMAN Engineering Company Kenya — fast delivery across Nairobi and Kenya.",
    "seller": {
      "@type": "LocalBusiness",
      "name": "ISMAN Engineering Company",
      "url": SITE_URL,
      "telephone": "+254724114555"
    },
    "areaServed": { "@type": "Country", "name": "Kenya" }
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Store", "item": SITE_URL + "/store" }
    ]
  }
];

export default function StorePage() {
  useSEO({
    title: 'Engineering Parts & Equipment Store | ISMAN Kenya — Spare Parts Nairobi',
    description: 'Shop engineering spare parts, consumables and industrial equipment from ISMAN Engineering Company Kenya. Fast delivery across Nairobi and Kenya.',
    keywords: 'engineering parts Kenya, industrial spare parts Nairobi, HVAC parts Kenya, engineering equipment store Kenya',
    canonical: SITE_URL + '/store',
    schema: STORE_SCHEMA,
  });

  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
  } = useCart();

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const handleAddToCart = (product: StoreProduct, qty = 1) => {
    addToCart(product, qty);
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleOrderComplete = (ref: string) => {
    setOrderRef(ref);
    clearCart(); // the checkout window now shows the confirmation + payment steps
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
    setOrderRef("");
  };

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />
      <AnnouncementBanners page="store" />
      <StoreHero totalItems={totalItems} onCartOpen={() => setIsCartOpen(true)} />
      <AnnouncementBanners page="store" placement="inline" />
      <ProductGrid
        onAddToCart={handleAddToCart}
        onCartOpen={() => setIsCartOpen(true)}
        totalItems={totalItems}
      />
      <Footer />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        subtotal={subtotal}
        onCheckout={handleCheckout}
      />

      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        cartItems={cartItems}
        subtotal={subtotal}
        onOrderComplete={handleOrderComplete}
      />

      {confirmationOpen && (
        <OrderConfirmation isOpen={confirmationOpen} orderRef={orderRef} onClose={handleConfirmationClose} totalItems={cartItems.length} />
      )}

      {/* Track order shortcut */}
      <a href="/store/order" className="fixed bottom-5 left-5 z-40 bg-white border border-gray-200 shadow-lg text-gray-700 hover:text-teal-700 text-xs font-semibold px-4 py-2.5 rounded-full flex items-center gap-1.5">
        <i className="ri-truck-line text-teal-600"></i> Track my order
      </a>
    </main>
  );
}
